import { formatDistanceToNow, format } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { getLanguage, getLocale, translate as t } from '@/lib/i18n';

export function formatPeriodRange(periodDays: number): string {
  if (periodDays === 0) return t("sur tout l'historique", 'across all time');
  if (periodDays === 1) return t('sur les 24 dernières heures', 'over the last 24 hours');
  return t(`sur les ${periodDays} derniers jours`, `over the last ${periodDays} days`);
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const abs = Math.abs(value);
  const compact = (divisor: number) =>
    (value / divisor).toLocaleString(getLocale(), { maximumFractionDigits: 1 });
  if (abs >= 1_000_000_000) return `${compact(1_000_000_000)}${t('Md', 'B')}`;
  if (abs >= 1_000_000) return `${compact(1_000_000)}M`;
  if (abs >= 1_000) return `${compact(1_000)}k`;
  return value.toLocaleString(getLocale());
}

export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return Math.round(value).toLocaleString(getLocale());
}

export function formatPct(
  value: number | null | undefined,
  { sign = true, digits = 2 }: { sign?: boolean; digits?: number } = {},
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const pct = value * 100;
  const prefix = sign && pct > 0 ? '+' : '';
  return `${prefix}${pct.toLocaleString(getLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return t('jamais', 'never');
  try {
    return formatDistanceToNow(new Date(iso), {
      addSuffix: true,
      locale: getLanguage() === 'en' ? enUS : fr,
    });
  } catch {
    return '—';
  }
}

export function formatDate(iso: string | null | undefined, pattern = 'dd MMM yyyy HH:mm'): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), pattern, { locale: getLanguage() === 'en' ? enUS : fr });
  } catch {
    return '—';
  }
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1_000) return `${ms} ms`;
  const s = Math.floor(ms / 1_000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const remSec = s % 60;
  if (m < 60) return `${m}m ${remSec}s`;
  const h = Math.floor(m / 60);
  const remMin = m % 60;
  return `${h}h ${remMin}m`;
}
